import { useEffect, useState } from 'react';import { useLocation, useNavigate } from 'react-router-dom';
import {
  collection,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where
} from 'firebase/firestore';
import {
  Bell,
  BookOpenCheck,
  CalendarDays,
  GraduationCap,
  Home,
  FileSignature,
  LogOut,
  Menu,
  MessageSquareText,
  UserRound,
  X
} from 'lucide-react';

import { useAuth } from '../../core/auth/AuthProvider';

import { db } from '../../core/firebase/firebase';

import {
  hasRegisteredNotificationToken,
  requestBrowserNotifications
} from '../../core/notifications/browserNotifications';

const MENU = [
  { id: 'dashboard', label: 'Tableau de bord', icon: Home, to: '/enseignant' },
  { id: 'classes', label: 'Mes classes', icon: GraduationCap, to: '/enseignant?view=classes' },
  { id: 'homework', label: 'Travail à faire', icon: BookOpenCheck, to: '/enseignant/travail' },
  { id: 'timetable', label: 'Emploi du temps', icon: CalendarDays, to: '/enseignant?view=timetable' },
  { id: 'requests', label: 'Demandes & communication', icon: MessageSquareText, to: '/enseignant?view=requests' },
  { id: 'contract', label: 'Mon contrat', icon: FileSignature, to: '/enseignant?view=contract' },
  { id: 'profile', label: 'Mon profil', icon: UserRound, to: '/enseignant?view=profile' }
];

export default function TeacherShell({ activePage = 'dashboard', children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [notificationRegistered, setNotificationRegistered] = useState(false);
  const [notificationBusy, setNotificationBusy] = useState(false);
  const [notificationPromptOpen, setNotificationPromptOpen] = useState(false);
  const [notificationNotice, setNotificationNotice] = useState('');
  const [teacherNotifications, setTeacherNotifications] = useState([]);
  const [notificationPanelOpen, setNotificationPanelOpen] = useState(false);

  useEffect(() => {
  if (!user?.uid) return undefined;

  const notificationsQuery = query(
    collection(db, 'notifications'),
    where('teacherId', '==', user.uid)
  );

  return onSnapshot(
    notificationsQuery,
    (snapshot) => {
      const rows = snapshot.docs
        .map((document) => ({
          id: document.id,
          ...document.data()
        }))
        .sort((a, b) => {
          const aTime =
            a.createdAt?.toMillis?.() ||
            a.createdAt?.seconds * 1000 ||
            0;

          const bTime =
            b.createdAt?.toMillis?.() ||
            b.createdAt?.seconds * 1000 ||
            0;

          return bTime - aTime;
        });

      setTeacherNotifications(rows);
    },
    (error) => {
      console.error(
        'TeacherShell — notifications :',
        error
      );
    }
  );
}, [user?.uid]);

const unreadNotificationCount =
  teacherNotifications.filter(
    (notification) => notification.read !== true
  ).length;
  
  useEffect(() => {
  let active = true;

  if (!user?.uid || !profile) {
    return undefined;
  }

  hasRegisteredNotificationToken({ user, db })
    .then((registered) => {
      if (!active) return;

      setNotificationRegistered(registered);

      if (registered) {
        setNotificationPromptOpen(false);
        return;
      }

      const snoozeKey =
        `le-defi:teacher-notification-prompt-snooze:${user.uid}`;

      const snoozeUntil = Number(
        localStorage.getItem(snoozeKey) || 0
      );

      if (Date.now() >= snoozeUntil) {
        setNotificationPromptOpen(true);
      }
    })
    .catch((error) => {
      console.error(
        'TeacherShell — vérification notifications :',
        error
      );
    });

  return () => {
    active = false;
  };
}, [user?.uid, profile]);

  const notice = profile?.teacherNotice || {};
  const teacherName =
    [notice.firstName, notice.lastName].filter(Boolean).join(' ') ||
    profile?.fullname ||
    profile?.fullName ||
    profile?.displayName ||
    'Enseignant';

  const initials = teacherName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('') || 'E';

  async function enableTeacherNotifications() {
  if (notificationBusy) return;

  setNotificationBusy(true);
  setNotificationNotice('');

  try {
    const result = await requestBrowserNotifications({
      user,
      profile,
      db
    });

    setNotificationRegistered(result.registered === true);
    setNotificationNotice(result.message || '');

    if (result.registered === true) {
      setNotificationPromptOpen(false);
    }
  } catch (error) {
    console.error(
      'TeacherShell — activation notifications :',
      error
    );

    setNotificationNotice(
      'Impossible d’activer les notifications pour le moment.'
    );
  } finally {
    setNotificationBusy(false);
  }
}

function remindTeacherNotificationsLater() {
  const snoozeKey =
    `le-defi:teacher-notification-prompt-snooze:${user.uid}`;

  localStorage.setItem(
    snoozeKey,
    String(Date.now() + 3 * 24 * 60 * 60 * 1000)
  );

  setNotificationPromptOpen(false);
}  

  function go(to) {
    setMobileMenuOpen(false);
    navigate(to);
  }

  return (
    <main className="parent-portal-v0 teacher-portal-v1">
    {notificationPromptOpen && (
  <div
    style={{
      position: 'fixed',
      inset: 0,
      zIndex: 9999,
      background: 'rgba(15, 23, 42, 0.55)',
      backdropFilter: 'blur(5px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 20
    }}
  >
    <div
      style={{
        position: 'relative',
        width: '100%',
        maxWidth: 440,
        background: '#fff',
        borderRadius: 24,
        padding: 30,
        boxShadow: '0 25px 70px rgba(15, 23, 42, 0.25)'
      }}
    >
      <button
        type="button"
        onClick={remindTeacherNotificationsLater}
        aria-label="Fermer"
        style={{
          position: 'absolute',
          top: 16,
          right: 16,
          border: 0,
          background: '#f1f5f9',
          width: 36,
          height: 36,
          borderRadius: 12,
          display: 'grid',
          placeItems: 'center',
          cursor: 'pointer'
        }}
      >
        <X size={18} />
      </button>

      <div
        style={{
          width: 58,
          height: 58,
          borderRadius: 18,
          background: '#eff6ff',
          color: '#2563eb',
          display: 'grid',
          placeItems: 'center',
          marginBottom: 20
        }}
      >
        <Bell size={28} />
      </div>

      <p className="eyebrow">NOTIFICATIONS</p>

      <h2 style={{ margin: '6px 0 10px' }}>
        Restez informé des actions importantes
      </h2>

      <p
        style={{
          color: '#64748b',
          lineHeight: 1.6,
          marginBottom: 22
        }}
      >
        Activez les notifications pour recevoir les informations
        importantes liées à vos classes, vos demandes et vos activités
        sur la plateforme.
      </p>

      {notificationNotice && (
        <div
          style={{
            padding: '10px 12px',
            borderRadius: 12,
            background: '#f8fafc',
            marginBottom: 14,
            fontSize: 14
          }}
        >
          {notificationNotice}
        </div>
      )}

      <button
        type="button"
        className="button button-primary"
        onClick={enableTeacherNotifications}
        disabled={notificationBusy || notificationRegistered}
        style={{
          width: '100%',
          justifyContent: 'center',
          marginBottom: 10
        }}
      >
        <Bell size={17} />
        {notificationBusy
          ? 'Activation…'
          : notificationRegistered
            ? 'Notifications activées'
            : 'Activer les notifications'}
      </button>

      <button
        type="button"
        className="button button-secondary"
        onClick={remindTeacherNotificationsLater}
        style={{
          width: '100%',
          justifyContent: 'center'
        }}
      >
        Plus tard
      </button>
    </div>
  </div>
)}
      {mobileMenuOpen && (
        <button
          className="parent-menu-scrim"
          aria-label="Fermer le menu"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      <aside className={`parent-sidebar-v0 ${mobileMenuOpen ? 'open' : ''}`}>
        <div className="parent-brand-v0">
          <img src="/logo.png" alt="Le Défi" />
          <div>
            <strong>Le Défi</strong>
            <span>Espace enseignant</span>
          </div>
          <button
            className="parent-close-menu"
            type="button"
            onClick={() => setMobileMenuOpen(false)}
            aria-label="Fermer le menu"
          >
            <X />
          </button>
        </div>

        <nav>
          {MENU.map(({ id, label, icon: Icon, to }) => {
            const routeActive =
              id === 'homework'
                ? location.pathname === '/enseignant/travail'
                : id === 'classes' && location.pathname.startsWith('/enseignant/classes/')
                  ? true
                  : activePage === id;

            return (
              <button
                key={id}
                type="button"
                className={routeActive ? 'active' : ''}
                onClick={() => go(to)}
              >
                <Icon />
                <span>{label}</span>
              </button>
            );
          })}
        </nav>

        <button className="parent-signout-v0" type="button" onClick={logout}>
          <LogOut />
          <span>Déconnexion</span>
        </button>
      </aside>

      <section className="parent-workspace-v0">
        <header className="parent-header-v0 teacher-header-v1">
          <button
            className="parent-mobile-menu"
            type="button"
            onClick={() => setMobileMenuOpen(true)}
            aria-label="Ouvrir le menu"
          >
            <Menu />
          </button>

          <div className="parent-selected-child-v0 teacher-account-pill-v1">
            <div className="child-avatar small">{initials}</div>
            <div className="teacher-account-pill-copy-v1">
              <span>Compte enseignant</span>
              <strong>{teacherName}</strong>
            </div>
          </div>

          <header className="parent-header-v0 teacher-header-v1">
  <button
    className="parent-mobile-menu"
    type="button"
    onClick={() => setMobileMenuOpen(true)}
    aria-label="Ouvrir le menu"
  >
    <Menu />
  </button>

  <div className="parent-selected-child-v0 teacher-account-pill-v1">
    <div className="child-avatar small">{initials}</div>

    <div className="teacher-account-pill-copy-v1">
      <span>Compte enseignant</span>
      <strong>{teacherName}</strong>
    </div>
  </div>

<div style={{ position: 'relative' }}>
  <button
    className={`parent-notification-button ${
      unreadNotificationCount > 0 ? 'enabled' : ''
    }`}
    type="button"
    onClick={() =>
      setNotificationPanelOpen((current) => !current)
    }
    title="Notifications"
    aria-label="Notifications"
  >
    <Bell />

    {unreadNotificationCount > 0 && (
      <span
        style={{
          position: 'absolute',
          top: '-5px',
          right: '-5px',
          minWidth: '18px',
          height: '18px',
          padding: '0 5px',
          borderRadius: '999px',
          background: '#dc2626',
          color: '#fff',
          fontSize: '11px',
          fontWeight: 700,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        {unreadNotificationCount > 99
          ? '99+'
          : unreadNotificationCount}
      </span>
    )}
  </button>

  {notificationPanelOpen && (
    <div
      style={{
        position: 'absolute',
        top: 'calc(100% + 10px)',
        right: 0,
        width: 'min(380px, calc(100vw - 30px))',
        maxHeight: '480px',
        overflowY: 'auto',
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: '18px',
        boxShadow:
          '0 20px 50px rgba(15, 23, 42, 0.18)',
        zIndex: 1000
      }}
    >
      <div
        style={{
          padding: '16px 18px',
          borderBottom: '1px solid #e5e7eb'
        }}
      >
        <strong>Notifications</strong>

        <div
          style={{
            marginTop: '4px',
            fontSize: '12px',
            color: '#64748b'
          }}
        >
          {unreadNotificationCount > 0
            ? `${unreadNotificationCount} non lue${
                unreadNotificationCount > 1 ? 's' : ''
              }`
            : 'Tout est à jour'}
        </div>
      </div>

      {teacherNotifications.length === 0 ? (
        <div
          style={{
            padding: '28px 18px',
            textAlign: 'center',
            color: '#64748b'
          }}
        >
          Aucune notification.
        </div>
      ) : (
        teacherNotifications
          .slice(0, 20)
          .map((notification) => (
            <button
              key={notification.id}
              type="button"
              onClick={async () => {
                if (notification.read !== true) {
                  try {
                    await updateDoc(
                      doc(
                        db,
                        'notifications',
                        notification.id
                      ),
                      {
                        read: true,
                        readAt: serverTimestamp()
                      }
                    );
                  } catch (error) {
                    console.error(
                      'Lecture notification enseignant :',
                      error
                    );
                  }
                }
              }}
              style={{
                width: '100%',
                border: 0,
                borderBottom: '1px solid #f1f5f9',
                padding: '14px 18px',
                background:
                  notification.read === true
                    ? '#fff'
                    : '#eff6ff',
                textAlign: 'left',
                cursor: 'pointer'
              }}
            >
              <div
                style={{
                  display: 'flex',
                  gap: '10px',
                  alignItems: 'flex-start'
                }}
              >
                {notification.read !== true && (
                  <span
                    style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      background: '#2563eb',
                      flexShrink: 0,
                      marginTop: '6px'
                    }}
                  />
                )}

                <div style={{ minWidth: 0 }}>
                  <strong
                    style={{
                      display: 'block',
                      fontSize: '14px',
                      color: '#0f172a'
                    }}
                  >
                    {notification.title ||
                      'Nouvelle notification'}
                  </strong>

                  <span
                    style={{
                      display: 'block',
                      marginTop: '5px',
                      fontSize: '13px',
                      lineHeight: 1.4,
                      color: '#475569'
                    }}
                  >
                    {notification.message || ''}
                  </span>

                  <small
                    style={{
                      display: 'block',
                      marginTop: '7px',
                      color: '#94a3b8'
                    }}
                  >
                    {notification.createdAt
                      ?.toDate?.()
                      ?.toLocaleString('fr-FR') || ''}
                  </small>
                </div>
              </div>
            </button>
          ))
      )}
    </div>
  )}
</div>
</header>
        </header>

        <div className="parent-page-v0">{children}</div>
      </section>
    </main>
  );
}
